<script setup lang="ts">
import { useTemplateRef } from 'vue'

import type { ScrollFadeOptions } from '../../../lib/scroll-fade'
import { vScrollFade } from '../v-scroll-fade'

/** When an area scrolls: `always`, or only below or from a breakpoint, for a split view that stacks into one scroll when narrow. */
type ScrollAreaWhen =
  | 'always'
  | 'below-md'
  | 'below-lg'
  | 'below-article-split'
  | 'from-lg'
  | 'from-article-split'

withDefaults(
  defineProps<{
    /** The element to render, for an area that is itself a landmark or an inline run. */
    as?: 'div' | 'nav' | 'span'
    /** `card` reaches 8px into the gutter with matching padding and a 12px top inset, so card shadows, focus rings and the first row are not clipped, and rounds its top corners concentric with the cards (their radius plus the 8px reach) · `none` leaves the edges to the caller. */
    corners?: 'card' | 'none'
    /** When the area scrolls; outside that range it lets its content overflow as a plain box. */
    when?: ScrollAreaWhen
    /** The edge fade's options; the bottom edge fades by default. */
    fade?: ScrollFadeOptions
  }>(),
  { as: 'div', corners: 'none', when: 'always', fade: undefined },
)

defineSlots<{
  /** The scrolling content. */
  default(): unknown
}>()

const root = useTemplateRef<HTMLElement>('root')

defineExpose({
  /** The scrolling element, for code that reads or sets its scroll position. */
  element: root,
})

const WHEN_CLASSES: Record<ScrollAreaWhen, string> = {
  always: 'overflow-x-hidden overflow-y-auto',
  'below-md': 'max-md:overflow-x-hidden max-md:overflow-y-auto',
  'below-lg': 'max-lg:overflow-x-hidden max-lg:overflow-y-auto',
  'below-article-split': '@max-article-split:overflow-x-hidden @max-article-split:overflow-y-auto',
  'from-lg': 'lg:overflow-x-hidden lg:overflow-y-auto',
  'from-article-split': '@article-split:overflow-x-hidden @article-split:overflow-y-auto',
}

const CORNER_CLASSES = {
  card: '-mx-4 px-4 pt-(--scroll-card-inset-top) rounded-t-8-out-4 sticky-edge-card',
  none: 'sticky-edge-flush',
} as const
</script>

<template>
  <component
    :is="as"
    ref="root"
    v-scroll-fade="fade"
    class="overscroll-contain"
    :class="[WHEN_CLASSES[when], CORNER_CLASSES[corners]]"
  >
    <slot />
  </component>
</template>
